/**
 * Route-level permission guard + smart landing page picker.
 *
 * Admin users hold every key so both helpers are effectively pass-through
 * for them — zero behaviour change for Vikram. Restricted users get
 * redirected away from routes they can't access, and land on their first
 * accessible page after login instead of the hard-coded Daily Brief.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canAccessPath, PATH_TO_PERM } from '../utils/permissions';

// Ordered list — first accessible path wins. Order mirrors the sidebar
// so a user's "home" matches what they see in navigation.
const LANDING_PRIORITY = [
  '/brief',
  '/pulse',
  '/warroom',
  '/ask',
  '/review',
  // Sales Analytics
  '/analysis/departments',
  '/analysis/festivals',
  '/analysis/peak-hours',
  '/analysis/salesperson',
  '/analysis/vendors',
  '/analysis/product-intel',
  '/analysis/returns',
  // Stock
  '/analysis/stock',
  '/analysis/product-deep-dive',
  '/analysis/dead-stock-recovery',
  '/analysis/vendor-scorecard',
  // Procurement
  '/procurement',
  '/procurement/vendor-360',
  '/procurement/festival-buying',
  '/procurement/logistics',
  // HR
  '/hr/overview',
  '/hr/attendance',
  '/hr/attrition',
  '/hr/performance',
  '/hr/workforce',
  '/hr/demographics',
  '/hr/interns',
  '/hr/ideal-employee',
  // Market
  '/analysis/market-intel',
  // Settings (admins only)
  '/settings',
  // Space Intel (permission-guarded)
  '/space-intel',
];

/** Resolve the first path in `LANDING_PRIORITY` the user can access.
 *  Falls back to a Simple-Dashboards page (Overall > Sales > Stocks) when
 *  the user has no granular permission but their category grants scoped
 *  dashboards. Accepts either the full user object (preferred) or a raw
 *  permissions array for backward-compat with older callers.
 */
export const pickLandingPath = (userOrPerms) => {
  const perms = Array.isArray(userOrPerms)
    ? userOrPerms
    : (userOrPerms?.permissions || []);
  const dashboards = Array.isArray(userOrPerms) ? [] : (userOrPerms?.dashboards || []);
  const isAdmin = !Array.isArray(userOrPerms) && (userOrPerms?.role || "").toUpperCase() === "ADMIN";
  // Non-admin scoped users: land on their first Simple Dashboard so the
  // scoped sidebar and the main page stay in sync. Admins keep their /brief
  // home even if they happen to hold `dashboards[]` for preview reasons.
  if (!isAdmin) {
    for (const name of ['overall', 'sales', 'stocks']) {
      if (dashboards.includes(name)) return `/scoped/${name}`;
    }
  }
  for (const p of LANDING_PRIORITY) {
    if (canAccessPath(perms, p)) return p;
  }
  return null; // no accessible page at all
};

/**
 * Wrap a route element to require a specific permission key.
 * If the user lacks it, redirect to their landing page (or /login if
 * they have no accessible page). Admins pass through unconditionally
 * since they hold every key.
 */
export const RequirePermission = ({ permKey, children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const perms = user.permissions || [];
  // Admin holds every key; this is just a documented fast-path — the
  // permission check below would produce the same result.
  if ((user.role || '').toUpperCase() === 'ADMIN') return children;

  if (!permKey || perms.includes(permKey)) return children;

  const landing = pickLandingPath(user);
  return <Navigate to={landing || '/login'} replace />;
};

/**
 * Landing component used as the index route.
 * Redirects to Daily Brief for admins (and any user with `brief` perm) or
 * to their first accessible page otherwise.
 */
export const Landing = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  // Admin behaviour is exactly the same as before: brief is in their perms,
  // so pickLandingPath returns '/brief' — same effective landing page.
  const landing = pickLandingPath(user);
  if (!landing) return <Navigate to="/login" replace />;
  return <Navigate to={landing} replace />;
};

/** Path → permission key convenience helper — thin wrapper around the map. */
export const permForPath = (path) => PATH_TO_PERM[path];
